import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const DISPLAY_DATE_FORMAT = 'DD.MM.YYYY';
export const DISPLAY_DATE_PLACEHOLDER = 'TT.MM.JJJJ';

const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
const DISPLAY_DATE_SHAPE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

/** Calendar years allowed for form logic (`parseInt('0020',10)` → 20 rejects “pseudo” years). */
export const MIN_CALENDAR_YEAR = 1900;
export const MAX_CALENDAR_YEAR = 9999;

/** True when ISO `YYYY-MM-DD` is a real calendar date and the four-digit year is 1000–9999. */
export function isPlausibleFormCalendarIso(value: string): boolean {
  const s = String(value).trim();
  if (!ISO_DATE_SHAPE.test(s)) return false;
  const yearNum = parseInt(s.slice(0, 4), 10);
  if (yearNum < MIN_CALENDAR_YEAR || yearNum > MAX_CALENDAR_YEAR) return false;
  return dayjs(s, ISO_DATE_FORMAT, true).isValid();
}

/**
 * Coerce store values for `input type="date"` (`value` is usually `YYYY-MM-DD` from the browser).
 * Keeps invalid-but-typed values so Yup can show errors without nuking the field.
 */
export function toHtmlDateFieldValue(value: string | Date | '' | null | undefined): string {
  if (value === '' || value == null) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : dayjs(value).format(ISO_DATE_FORMAT);
  }
  const s = String(value).trim();
  if (!s) return '';
  if (ISO_DATE_SHAPE.test(s)) {
    return s;
  }
  const parsed = dayjs(s);
  return parsed.isValid() ? parsed.format(ISO_DATE_FORMAT) : '';
}

/** Coerce store values to German `DD.MM.YYYY` for text inputs. */
export function toGermanDisplayDateValue(value: string | Date | '' | null | undefined): string {
  if (value === '' || value == null) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : dayjs(value).format(DISPLAY_DATE_FORMAT);
  }
  const s = String(value).trim();
  if (!s) return '';
  if (DISPLAY_DATE_SHAPE.test(s)) return s;
  if (ISO_DATE_SHAPE.test(s)) return isoToDisplay(s);
  const normalizedDisplayIso = displayToIso(s);
  if (normalizedDisplayIso) return isoToDisplay(normalizedDisplayIso);
  const parsed = dayjs(s, [DISPLAY_DATE_FORMAT, ISO_DATE_FORMAT], true);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : s;
}

function splitGermanDateSegments(value: string): string[] {
  return value.replace(/[^\d.]/g, '').split('.');
}

function isCompleteGermanDisplayDate(value: string): boolean {
  const [day, month, year] = splitGermanDateSegments(value);
  return Boolean(day && month && year && year.length === 4);
}

/** Insert `.` delimiters while the user types digits (`12031990` → `12.03.1990`). */
export function maskGermanDateInput(raw: string, previous?: string): string {
  const trimmed = raw.trim();
  if (ISO_DATE_SHAPE.test(trimmed)) {
    const display = isoToDisplay(trimmed);
    if (display) return display;
  }
  const prevDigits = (previous ?? '').replace(/\D/g, '');
  const digits = raw.replace(/\D/g, '').slice(0, 8);

  // Backspace on a trailing `.` (e.g. `12.` → `12`) should delete the last digit, not re-add the dot.
  if (previous && raw.length < previous.length && digits.length === prevDigits.length && digits.length > 0) {
    return formatGermanDateDigits(digits.slice(0, -1));
  }

  // Keep existing segment boundaries when editing a formatted value. Rechunking all digits would move values
  // between day, month, and year after a selection replacement (`01.01.2001` → `4.01.2001`).
  if (trimmed.includes('.')) {
    const parts = splitGermanDateSegments(trimmed);
    const previousParts = previous ? splitGermanDateSegments(previous) : [];

    // Jumping into the year block can temporarily drop the day segment (`12.03.1990` → `03.1990`).
    if (previous && isCompleteGermanDisplayDate(previous) && parts.length === 2) {
      const [maybeMonth, maybeYear] = parts;
      const previousMonth = previousParts[1]?.slice(0, 2) ?? '';
      const previousDay = previousParts[0]?.slice(0, 2) ?? '';
      if (maybeMonth.slice(0, 2) === previousMonth) {
        const month = maybeMonth.slice(0, 2);
        const year = (maybeYear ?? '').slice(0, 4);
        if (!year) return `${previousDay}.${month}.`;
        return `${previousDay}.${month}.${year}`;
      }
    }

    const [rawDay = '', rawMonth, rawYear] = parts;
    const day = rawDay.slice(0, 2);
    if (rawMonth === undefined) return day;

    const month = rawMonth.slice(0, 2);
    if (rawYear === undefined) return month.length === 2 ? `${day}.${month}.` : `${day}.${month}`;

    return `${day}.${month}.${rawYear.slice(0, 4)}`;
  }

  if (previous && isCompleteGermanDisplayDate(previous) && digits.length > 0 && digits.length <= 4) {
    const [previousDay, previousMonth] = splitGermanDateSegments(previous);
    return `${previousDay.slice(0, 2)}.${previousMonth.slice(0, 2)}.${digits}`;
  }

  return formatGermanDateDigits(digits);
}

function formatGermanDateDigits(digits: string): string {
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (digits.length <= 1) return digits;
  if (digits.length === 2) return `${day}.`;
  if (digits.length === 3) return `${day}.${month}`;
  if (digits.length === 4) return `${day}.${month}.`;
  return `${day}.${month}.${year}`;
}

/** Map masked German display to the ISO value stored in Formik / order state. */
export function toStoredDateValue(display: string): string {
  const masked = maskGermanDateInput(display);
  if (!masked) return '';
  const iso = displayToIso(masked);
  return iso || masked;
}

/** Convert stored ISO date (`YYYY-MM-DD`) to German display (`DD.MM.YYYY`). */
export function isoToDisplay(iso: string): string {
  if (!iso?.trim()) return '';
  const t = iso.trim();
  if (!ISO_DATE_SHAPE.test(t)) return '';
  const yearNum = parseInt(t.slice(0, 4), 10);
  if (yearNum < MIN_CALENDAR_YEAR || yearNum > MAX_CALENDAR_YEAR) return '';
  const parsed = dayjs(t, ISO_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : '';
}

/** Parse German display input to ISO `YYYY-MM-DD`, or empty string if invalid / empty. */
export function displayToIso(display: string): string {
  const trimmed = display.trim();
  if (!trimmed) return '';
  const m = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return '';
  const yearNum = parseInt(m[3], 10);
  if (yearNum < MIN_CALENDAR_YEAR || yearNum > MAX_CALENDAR_YEAR) return '';
  const normalized = `${m[1].padStart(2, '0')}.${m[2].padStart(2, '0')}.${m[3]}`;
  const parsed = dayjs(normalized, DISPLAY_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(ISO_DATE_FORMAT) : '';
}
